// Definição declarativa das rotas. Lazy-load por feature pra reduzir bundle.
// Paths centralizados em `./paths` — nunca duplicar string aqui.
import { createElement, lazy, Suspense } from 'react';
import { type RouteObject, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorFallback } from '@/components/layout/ErrorFallback';
import { Loader } from '@/components/ui/Loader';
import {
  LandingRedirect,
  RequireAuth,
  RequireGuest,
  RequireOnboarding,
  RequireRole,
} from './guards';
import { PDV_ROLES, PRODUCTION_ROLES } from '@/config/constants';
import { ROUTES } from './paths';

const AuthLayout = lazy(() => import('@/features/auth/pages/shared/AuthLayout'));
const LegalLayout = lazy(() => import('@/features/legal/pages/shared/LegalLayout'));
const TermsPage = lazy(() => import('@/features/legal/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/features/legal/pages/PrivacyPage'));
const DataDeletionPage = lazy(() => import('@/features/legal/pages/DataDeletionPage'));
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const SignupPage = lazy(() => import('@/features/auth/pages/SignupPage'));
const OnboardingPage = lazy(() => import('@/features/auth/pages/OnboardingPage'));
const InviteAcceptPage = lazy(() => import('@/features/auth/pages/InviteAcceptPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const NetworkPage = lazy(() => import('@/features/stores/pages/NetworkPage'));
const StoreDrilldownPage = lazy(() => import('@/features/stores/pages/StoreDrilldownPage'));
const CreateStorePage = lazy(() => import('@/features/stores/pages/CreateStorePage'));
const InboxPage = lazy(() => import('@/features/inbox/pages/InboxPage'));
const ContactsPage = lazy(() => import('@/features/contacts/pages/ContactsPage'));
const CrmPage = lazy(() => import('@/features/crm/pages/CrmPage'));
const CampanhasPage = lazy(() => import('@/features/campanhas/pages/CampanhasPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const MetasPage = lazy(() => import('@/features/goals/pages/MetasPage'));
// Caminho completo, não o barrel `@/features/metrics`: o barrel é o que o inbox
// importa (MET-16) e não pode arrastar as views pro chunk dele.
const MetricsPage = lazy(() => import('@/features/metrics/pages/MetricsPage'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const GuichePage = lazy(() => import('@/features/pdv/pages/GuichePage'));
const ProdutosPage = lazy(() => import('@/features/pdv/pages/ProdutosPage'));
const ProducaoPage = lazy(() => import('@/features/production/pages/ProducaoPage'));

function PageFallback() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
      <Loader />
    </div>
  );
}

function lazyRoute(node: React.ReactNode) {
  return <Suspense fallback={<PageFallback />}>{node}</Suspense>;
}

const errorElement = (
  <ErrorFallback
    error={new Error('Erro na navegação')}
    resetErrorBoundary={() => window.location.reload()}
  />
);

export const routes: RouteObject[] = [
  // Layout de auth: AuthShell + AuthSideArt montam UMA vez e persistem entre as
  // páginas (login/signup/reset/invite). Só o <Outlet/> do formulário troca, então
  // a animação do painel direito não reinicia ao navegar. F5 remonta tudo (reseta).
  {
    element: lazyRoute(<AuthLayout />),
    errorElement,
    children: [
      {
        path: ROUTES.login,
        element: (
          <RequireGuest>
            <LoginPage />
          </RequireGuest>
        ),
      },
      {
        path: ROUTES.signup,
        element: (
          <RequireGuest>
            <SignupPage />
          </RequireGuest>
        ),
      },
      { path: ROUTES.inviteAccept, element: <InviteAcceptPage /> },
      { path: ROUTES.passwordReset, element: <ResetPasswordPage /> },
    ],
  },
  {
    path: ROUTES.onboarding,
    element: lazyRoute(
      <RequireAuth>
        <RequireRole min="GESTOR">
          <RequireOnboarding incomplete>
            <OnboardingPage />
          </RequireOnboarding>
        </RequireRole>
      </RequireAuth>,
    ),
    errorElement,
  },
  {
    path: ROUTES.root,
    element: (
      <RequireAuth>
        <RequireOnboarding>
          <AppShell />
        </RequireOnboarding>
      </RequireAuth>
    ),
    errorElement,
    children: [
      // Landing page pós-login: Dashboard pessoal — exceto pro ATENDENTE, que a
      // LandingRedirect manda pro guichê (RF-01).
      {
        index: true,
        element: <LandingRedirect>{lazyRoute(<DashboardPage />)}</LandingRedirect>,
      },
      // Configurações do usuário — página pessoal, acessível a qualquer role
      // autenticado (sem RequireRole).
      { path: ROUTES.config, element: lazyRoute(<SettingsPage />) },
      // "Minha visão" é a única tela fora do módulo presencial que o ATENDENTE vê
      // (é assim no protótipo). As demais rotas seguem excluindo o papel.
      {
        path: ROUTES.dashboard,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR', 'ATENDENTE']}>
            <DashboardPage />
          </RequireRole>,
        ),
      },
      // PDV / Guichê (RF-01..RF-08). Allow-list explícita — a hierarquia de rank
      // não expressa "esses três papéis" (o ATENDENTE tem o rank mais baixo).
      {
        path: ROUTES.guiche,
        element: lazyRoute(
          <RequireRole oneOf={PDV_ROLES}>
            <GuichePage />
          </RequireRole>,
        ),
      },
      // Catálogo só-leitura do atendente.
      {
        path: ROUTES.produtos,
        element: lazyRoute(
          <RequireRole oneOf={['ATENDENTE']}>
            <ProdutosPage />
          </RequireRole>,
        ),
      },
      // Produção (RF-04). Allow-list explícita, pelo mesmo motivo do guichê: o
      // PRODUCAO tem o rank mais baixo e nenhuma hierarquia o descreve. É a ÚNICA
      // rota do papel — todas as demais são `oneOf` que não o listam.
      {
        path: ROUTES.producao,
        element: lazyRoute(
          <RequireRole oneOf={PRODUCTION_ROLES}>
            <ProducaoPage />
          </RequireRole>,
        ),
      },
      // Metas de venda (RF-17/18/19) — só gestor/gerente. A aba ativa vive na
      // URL (/metas/:tab). O segmento estático não colide com outras rotas.
      {
        path: ROUTES.metas,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE']}>
            <MetasPage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.metasTab,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE']}>
            <MetasPage />
          </RequireRole>,
        ),
      },
      // Indicadores de atendimento (MET-01..MET-28). O VENDEDOR entra na allow-list
      // porque MET-27 exige que ele veja os PRÓPRIOS números; o recorte é feito por
      // clamp no selector (`selectMetricsQueryArgs`), não por rota — assim não há
      // como um componente esquecer o gate. ATENDENTE e PRODUCAO ficam de fora: o
      // posto deles não tem indicador de conversa.
      {
        path: ROUTES.indicadores,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <MetricsPage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.indicadoresTab,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <MetricsPage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.inbox,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <InboxPage />
          </RequireRole>,
        ),
      },
      // Fila virou aba dentro de Conversas. Segmento estático vence o dinâmico
      // /conversas/:conversationId no ranking do react-router v6.
      {
        path: ROUTES.inboxQueue,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <InboxPage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.inboxConversation,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <InboxPage />
          </RequireRole>,
        ),
      },
      // /fila legado → redireciona pra aba da fila (compat com links/bookmarks).
      { path: ROUTES.queue, element: <Navigate to={ROUTES.inboxQueue} replace /> },
      {
        path: ROUTES.contacts,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <ContactsPage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.contactDetail,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <ContactsPage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.crm,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <CrmPage />
          </RequireRole>,
        ),
      },
      {
        // Mesma página: a aba vem da URL, que é o que faz o quadro aberto
        // sobreviver ao F5 e ser compartilhável.
        path: ROUTES.crmTab,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <CrmPage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.campanhas,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <CampanhasPage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.campanhaDetail,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <CampanhasPage />
          </RequireRole>,
        ),
      },
      {
        // Builder do rascunho. Mesma página: o passo vem da URL, e é isso que faz
        // o fluxo sobreviver ao F5 e ser compartilhável.
        path: ROUTES.campanhaEditStep,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE', 'VENDEDOR']}>
            <CampanhasPage />
          </RequireRole>,
        ),
      },
      // Painel de rede (Empresas). Segmentos estáticos (/nova, /loja/...) vencem
      // o dinâmico /empresas/:tab no ranking do react-router v6.
      {
        path: ROUTES.storesNew,
        element: lazyRoute(
          <RequireRole min="GESTOR">
            <CreateStorePage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.storeDrilldown,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE']}>
            <StoreDrilldownPage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.storeDrilldownTab,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE']}>
            <StoreDrilldownPage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.stores,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE']}>
            <NetworkPage />
          </RequireRole>,
        ),
      },
      {
        path: ROUTES.storesTab,
        element: lazyRoute(
          <RequireRole oneOf={['GESTOR', 'GERENTE']}>
            <NetworkPage />
          </RequireRole>,
        ),
      },
    ],
  },
  // Páginas institucionais públicas. Sem guard: acessíveis deslogado (Meta/crawlers)
  // e logado (sem redirect). O LegalLayout monta o chrome de leitura e provê o
  // Suspense interno do <Outlet/> que troca Termos↔Privacidade.
  {
    element: lazyRoute(<LegalLayout />),
    errorElement,
    children: [
      { path: ROUTES.terms, element: <TermsPage /> },
      { path: ROUTES.privacy, element: <PrivacyPage /> },
      { path: ROUTES.dataDeletion, element: <DataDeletionPage /> },
    ],
  },
  // Laboratório de notificações. Sem guard e fora do AppShell: o ToastProvider
  // mora em AppProviders, acima do router, então a tela roda deslogada e sem
  // banco — que é o ponto, já que ela existe pra olhar movimento, não dado.
  //
  // 🔴 O `lazy(() => import(...))` fica DENTRO do ramo, não no topo do arquivo.
  // Declarado lá em cima, o `import()` é estaticamente alcançável e o Rollup
  // emite o chunk mesmo com a rota em código morto — foi o que aconteceu na
  // primeira tentativa (`dist/assets/ToastLabPage-*.js` no build de produção).
  // Aqui `import.meta.env.DEV` vira `false` na substituição, o ramo inteiro é
  // descartado e o módulo deixa de ter qualquer referência.
  // Precisa vir ANTES do catch-all, senão o `*` engole a URL.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/dev/toasts',
          element: lazyRoute(createElement(lazy(() => import('@/features/dev/pages/ToastLabPage')))),
          errorElement,
        },
        {
          path: '/dev/importacao-historico',
          element: lazyRoute(
            createElement(lazy(() => import('@/features/dev/pages/ImportHistoryLabPage'))),
          ),
          errorElement,
        },
      ]
    : []),
  // Catch-all: cada papel volta pra própria casa (o ATENDENTE, pro guichê).
  { path: '*', element: <LandingRedirect /> },
];
